import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TruckIcon, ArrowRightIcon, PhoneIcon, CalendarIcon, MapPinIcon, SettingsIcon, CheckCircle2Icon, ClockIcon, HelpCircleIcon, ClipboardListIcon, ShieldCheckIcon, WifiIcon, PhoneCallIcon, CableIcon, ServerIcon, ZapIcon, QuoteIcon } from 'lucide-react';
const checklist = [{
  time: '30 Days Out',
  title: 'Notify Ambient Telecom',
  description: 'Let us know your move date and new address so we can begin the service availability check.'
}, {
  time: '15 Days Out',
  title: 'New Site Audit',
  description: 'Our team audits the new location to ensure the necessary infrastructure is in place for your services.'
}, {
  time: '7 Days Out',
  title: 'Confirm Installation',
  description: 'We finalize the installation schedule and confirm all equipment is ready for the transition.'
}, {
  time: 'Move Day',
  title: 'Final Connectivity Check',
  description: 'Our technicians perform a final check to ensure all services are live and functioning perfectly.'
}];
const faqs = [{
  question: 'Can I keep my current phone numbers?',
  answer: 'Yes! In most cases, we can port your existing numbers to your new location seamlessly so your customers never lose touch.'
}, {
  question: 'How long does a typical installation take?',
  answer: 'Standard installations usually take 3-5 business days, but we recommend starting the process 30 days before your move to ensure zero downtime.'
}, {
  question: 'Is there a fee for moving my services?',
  answer: 'Ambient Telecom often waives move fees for existing customers. We will review your current contract and provide a transparent cost breakdown.'
}, {
  question: "What if my new location doesn't have fiber?",
  answer: "We work with over 240 suppliers. If fiber isn't available, we will find the best alternative (Fixed Wireless, Coax, etc.) to meet your needs."
}];
const whatWeHandle = [{
  icon: WifiIcon,
  title: 'Internet Transfer',
  desc: 'Seamlessly move or upgrade your broadband connection.'
}, {
  icon: PhoneCallIcon,
  title: 'Phone Number Porting',
  desc: 'Keep your existing numbers without interruption.'
}, {
  icon: ShieldCheckIcon,
  title: 'Security System Relocation',
  desc: 'Transfer and optimize your surveillance and access control.'
}, {
  icon: CableIcon,
  title: 'Cabling at New Site',
  desc: 'Professional Cat5/Cat6 and fiber optic installation.'
}, {
  icon: ServerIcon,
  title: 'Equipment Setup',
  desc: 'Rack and stack servers, switches, and network gear.'
}, {
  icon: ZapIcon,
  title: 'Service Optimization',
  desc: 'Ensure all systems are running at peak performance.'
}];
const movingStats = [{
  value: '500+',
  label: 'Successful Transitions'
}, {
  value: 'Zero',
  label: 'Downtime Guarantee'
}, {
  value: '48hr',
  label: 'Average Setup Time'
}, {
  value: '100%',
  label: 'Number Retention Rate'
}];
const testimonials = [{
  quote: "Moving our law firm was stressful enough, but Ambient Telecom handled our entire IT and phone system transition flawlessly. We didn't miss a single client call.",
  author: 'David R.',
  role: 'Managing Partner, Regional Law Firm'
}, {
  quote: 'They coordinated the internet installation and cabling for our new retail location perfectly. Everything was up and running days before our grand opening.',
  author: 'Jessica T.',
  role: 'Operations Manager, Retail Chain'
}];
export function MovingSoonPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
  };
  return <div className="bg-white" data-id="element-1597">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-1598">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1599"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1600"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" data-id="element-1601"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1602">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1603">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1604">
              <TruckIcon className="w-4 h-4 mr-2 text-primary" data-id="element-1605" />
              Seamless Transitions
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1606">
              Business Moving?
              <br className="hidden md:block" data-id="element-1607" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1608">
                We've Got You.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-1609">
              Moving to a new location? Let us take the stress off your
              shoulders. Our team handles your telecom transition from start to
              finish.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-1610">
              <a href="#transition-form" className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1611">
                Start Your Transition
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1612" />
              </a>
              <a href="tel:+4435451747" className="flex items-center justify-center text-slate-700 font-medium py-2" data-id="element-1613">
                <PhoneIcon className="w-5 h-5 mr-2 text-primary" data-id="element-1614" />
                (443) 545-1747
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="py-24 bg-white" data-id="element-1615">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1616">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1617">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-1618">
              <ClipboardListIcon className="w-8 h-8 text-primary" data-id="element-1619" />
              Telecom Moving Checklist
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1620">
              Follow our proven timeline to ensure your business stays connected
              during your transition.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1621">
            {checklist.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-slate-50 p-8 rounded-2xl border border-slate-100 relative" data-id="element-1622">
                <div className="text-primary font-black text-sm uppercase tracking-widest mb-4" data-id="element-1623">
                  {item.time}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1624">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1625">
                  {item.description}
                </p>
                {i < checklist.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10" data-id="element-1626">
                    <ArrowRightIcon className="w-6 h-6 text-slate-300" data-id="element-1627" />
                  </div>}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* What We Handle During Your Move */}
      <section className="py-24 bg-slate-50" data-id="element-1628">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1629">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1630">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1631">
              What We Handle During Your Move
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1632">
              We take care of every technical detail so you can focus on your
              business operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1633">
            {whatWeHandle.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1634">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6" data-id="element-1635">
                  <item.icon className="w-6 h-6 text-primary" data-id="element-1636" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1637">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1638">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Transition Form Section */}
      <section className="py-20 bg-white" data-id="element-1639">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1640">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1641">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1642">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1643">
                Let Us Handle the Heavy Lifting
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-1644">
                Our team can help you transition from your current location to
                your new place seamlessly — without you having to lift a finger.
                From planning to setup, we handle the details so you can focus
                on settling into your new space.
              </p>

              <div className="space-y-6" data-id="element-1645">
                <h3 className="text-xl font-bold text-slate-900" data-id="element-1646">
                  How It Works
                </h3>
                <div className="space-y-4" data-id="element-1647">
                  {['Provide your current and new address details', 'List your current services and future needs', 'Set your preferred move date', 'Our team reviews and coordinates the entire setup', 'Enjoy seamless connectivity at your new location'].map((step, i) => <div key={i} className="flex items-start gap-3" data-id="element-1648">
                      <div className="mt-1 bg-primary/10 rounded-full p-1" data-id="element-1649">
                        <CheckCircle2Icon className="w-4 h-4 text-primary" data-id="element-1650" />
                      </div>
                      <span className="text-slate-700" data-id="element-1651">{step}</span>
                    </div>)}
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} id="transition-form" className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200 shadow-xl" data-id="element-1652">
              <AnimatePresence mode="wait" data-id="element-1653">
                {!formSubmitted ? <motion.form key="form" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onSubmit={handleSubmit} className="space-y-6" data-id="element-1654">
                    <div className="text-center mb-8" data-id="element-1655">
                      <h3 className="text-2xl font-bold text-slate-900" data-id="element-1656">
                        Transition Details
                      </h3>
                      <p className="text-slate-500 mt-2" data-id="element-1657">
                        Tell us about your upcoming move
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1658">
                      <div className="space-y-2" data-id="element-1659">
                        <label className="text-sm font-bold text-slate-700 flex items-center gap-2" data-id="element-1660">
                          <MapPinIcon className="w-4 h-4 text-primary" data-id="element-1661" />
                          Current Address
                        </label>
                        <input required type="text" placeholder="Street, City, State, ZIP" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all" data-id="element-1662" />
                      </div>
                      <div className="space-y-2" data-id="element-1663">
                        <label className="text-sm font-bold text-slate-700 flex items-center gap-2" data-id="element-1664">
                          <MapPinIcon className="w-4 h-4 text-primary" data-id="element-1665" />
                          New Address
                        </label>
                        <input required type="text" placeholder="Street, City, State, ZIP" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all" data-id="element-1666" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1667">
                      <div className="space-y-2" data-id="element-1668">
                        <label className="text-sm font-bold text-slate-700 flex items-center gap-2" data-id="element-1669">
                          <SettingsIcon className="w-4 h-4 text-primary" data-id="element-1670" />
                          Current Services
                        </label>
                        <input required type="text" placeholder="e.g. Fiber, VoIP, Security" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all" data-id="element-1671" />
                      </div>
                      <div className="space-y-2" data-id="element-1672">
                        <label className="text-sm font-bold text-slate-700 flex items-center gap-2" data-id="element-1673">
                          <CalendarIcon className="w-4 h-4 text-primary" data-id="element-1674" />
                          Move Date
                        </label>
                        <input required type="date" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all" data-id="element-1675" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-1676">
                      <label className="text-sm font-bold text-slate-700 flex items-center gap-2" data-id="element-1677">
                        <ArrowRightIcon className="w-4 h-4 text-primary" data-id="element-1678" />
                        Services Needed at New Location
                      </label>
                      <textarea rows={3} placeholder="What would you like installed or arranged?" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none" data-id="element-1679"></textarea>
                    </div>

                    <button type="submit" className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-primary/20 flex items-center justify-center gap-2" data-id="element-1680">
                      Submit Transition Request
                      <ArrowRightIcon className="w-5 h-5" data-id="element-1681" />
                    </button>
                  </motion.form> : <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="text-center py-12" data-id="element-1682">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1683">
                      <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-1684" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1685">
                      Request Received!
                    </h3>
                    <p className="text-slate-600 mb-8" data-id="element-1686">
                      Our team is reviewing your transition details. We'll be in
                      touch shortly to coordinate your move.
                    </p>
                    <button onClick={() => setFormSubmitted(false)} className="text-primary font-bold hover:underline" data-id="element-1687">
                      Submit another request
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Moving Success Stats */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden" data-id="element-1688">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1689"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1690">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-1691">
            {movingStats.map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} data-id="element-1692">
                <div className="text-4xl md:text-5xl font-extrabold text-primary mb-2" data-id="element-1693">
                  {stat.value}
                </div>
                <div className="text-slate-300 font-medium" data-id="element-1694">{stat.label}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Moving FAQ Section */}
      <section className="py-24 bg-slate-50" data-id="element-1695">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1696">
          <div className="text-center mb-16" data-id="element-1697">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-1698">
              <HelpCircleIcon className="w-8 h-8 text-primary" data-id="element-1699" />
              Moving FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1700">
              Common questions about transitioning your telecom services.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1701">
            {faqs.map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden" data-id="element-1702">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-1703">
                  <span className="font-bold text-slate-900" data-id="element-1704">
                    {faq.question}
                  </span>
                  <div className={`transform transition-transform ${activeFaq === i ? 'rotate-180' : ''}`} data-id="element-1705">
                    <ArrowRightIcon className="w-5 h-5 text-slate-400 rotate-90" data-id="element-1706" />
                  </div>
                </button>
                <AnimatePresence data-id="element-1707">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-1708">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100 bg-slate-50/50" data-id="element-1709">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* Businesses Trust Us With Their Moves */}
      <section className="py-24 bg-white" data-id="element-1710">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1711">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1712">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1713">
              Businesses Trust Us With Their Moves
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1714">
              Don't just take our word for it. See what our clients have to say
              about their transition experience.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1715">
            {testimonials.map((testimonial, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-slate-50 p-10 rounded-3xl border border-slate-200 relative" data-id="element-1716">
                <QuoteIcon className="w-10 h-10 text-primary/20 absolute top-8 left-8" data-id="element-1717" />
                <div className="relative z-10" data-id="element-1718">
                  <p className="text-lg text-slate-700 italic mb-8 pt-4" data-id="element-1719">
                    "{testimonial.quote}"
                  </p>
                  <div data-id="element-1720">
                    <div className="font-bold text-slate-900" data-id="element-1721">
                      {testimonial.author}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-1722">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Speak with Consultant */}
      <section className="py-20 bg-slate-50 border-t border-slate-200" data-id="element-1723">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1724">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1725">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1726">
              Speak With a Consultant
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1727">
              Our consultants will guide you through the process and make sure
              everything is handled smoothly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1728">
            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm text-center" data-id="element-1729">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-1730">
                <PhoneIcon className="w-7 h-7 text-primary" data-id="element-1731" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1732">
                Call Us Directly
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-1733">
                Get immediate assistance with your transition planning.
              </p>
              <a href="tel:+4435451747" className="inline-flex items-center text-primary font-bold text-lg hover:underline" data-id="element-1734">
                443-545-1747
              </a>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm text-center" data-id="element-1735">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-1736">
                <ClockIcon className="w-7 h-7 text-indigo-600" data-id="element-1737" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1738">
                Schedule a Callback
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-1739">
                Pick a time that works best for your schedule.
              </p>
              <button onClick={() => onNavigate('contact')} className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors" data-id="element-1740">
                Schedule Now
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-1741">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1742"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-1743">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1744">
            Book Your Free Consultation
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1745">
            Set up a time with one of our consultants today and let us help make
            your move simple, organized, and stress-free.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-50 transition-all shadow-xl" data-id="element-1746">
            Schedule Your Free Consultation
          </button>
        </div>
      </section>
    </div>;
}